import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '../ui/Button';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import { localToday } from '../../utils/date';

// Walkie-talkie mode — hands-free, but YOU decide when your turn ends, not a silence timer.
// You talk as long as you like; each silence-delimited segment is transcribed and appended
// to a live on-screen transcript. The turn only ships when you say the send word
// ("over over" by default) OR tap the big SEND button. While the reply speaks you can tap
// INTERRUPT to cut it off and start talking again. This is push-to-talk turn-taking without
// touching the screen — total control of the conversation, hands-free.
//
// Reuses the proven Phase-E plumbing (RMS energy VAD + MediaRecorder + server Whisper +
// browser TTS) — no new audio tech, so none of the CSP/WASM risk that killed Silero. The
// only real difference from Auto mode: a segment no longer ends the turn; it's transcribed
// and buffered, and we watch the buffer's tail for the send word. Auto mode = silence ends
// your turn; Walkie-talkie = the send word (or the button) ends it.

const CATEGORY_EMOJI = {
  exercise: '🏃', nutrition: '🍽️', sleep: '😴', biometric: '📏', mental_health: '🧠',
  therapy: '🌿', medical: '🩺', environmental: '🌤️', social: '👥', other: '•',
};

const entryLabel = (e) => {
  const emoji = CATEGORY_EMOJI[(e.category_slug || '').split('.')[0]] || '•';
  const name = (e.entry_type || '').replace(/_/g, ' ');
  let detail = '';
  if (e.value != null) detail = ` ${e.value}${e.value_unit ? ' ' + e.value_unit : ''}`;
  else if (e.calories != null) detail = ` ${Math.round(e.calories)} cal`;
  else if (e.duration_minutes != null) detail = ` ${e.duration_minutes} min`;
  return `${emoji} ${name}${detail}`;
};

// Strip emoji/symbols before speaking — a TTS voice reads 🏃 literally. Displayed text keeps
// them; only the spoken version is cleaned. (Same rule as Auto mode.)
const stripForSpeech = (t) => (t || '')
  .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE00}-\u{FE0F}\u{200D}]/gu, '')
  .replace(/\s{2,}/g, ' ')
  .trim();

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

// --- Send-word matching -----------------------------------------------------------------
// Normalize to lowercase words so "Over, over." matches "over over". We only trigger when
// the send word is the TAIL of the buffer, so saying it mid-sentence is unlikely to misfire.
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const normalize = (t) => (t || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

const endsWithSendWord = (text, word) => {
  const n = normalize(text);
  const w = normalize(word);
  if (!w) return false;
  return n === w || n.endsWith(` ${w}`);
};

// Remove the trailing send-word occurrence (with whatever punctuation/spacing STT emitted).
const stripSendWord = (text, word) => {
  const tokens = normalize(word).split(' ').filter(Boolean);
  if (!tokens.length) return (text || '').trim();
  const pattern = `${tokens.map(escapeRegex).join('[\\s,.!?]+')}[\\s,.!?]*$`;
  return (text || '').replace(new RegExp(pattern, 'i'), '').trim();
};

// --- Turn-detection tuning --------------------------------------------------------------
const POLL_MS = 50;               // energy sampled 20x/sec
const SPEECH_RMS = 0.018;         // RMS above this = voice (quiet room ~0.002, speech ~0.05+)
// Shorter than Auto mode's 3s: silence no longer ends the turn, it only closes one segment
// so it can be transcribed and shown. A snappier segment means the send word is caught and
// the live transcript updates sooner.
const SILENCE_HANG_MS = 1500;
const MIN_UTTERANCE_MS = 350;     // shorter than this = a cough/clack, discard
const MAX_UTTERANCE_MS = 15000;   // force-close a segment so it still transcribes in chunks
const MAX_UPLOAD_BYTES = 1000 * 1024;

const SEND_WORD_KEY = 'vg_walkie_send_word';
const DEFAULT_SEND_WORD = 'over over';

export const getSendWord = () => {
  try {
    const v = (localStorage.getItem(SEND_WORD_KEY) || '').trim();
    return v || DEFAULT_SEND_WORD;
  } catch (e) { return DEFAULT_SEND_WORD; }
};

const WalkieTalkie = ({ onClose, onEntrySaved, defaultDate }) => {
  // connecting → listening → thinking → speaking → listening … | error
  const [status, setStatus] = useState('connecting');
  const [userSpeaking, setUserSpeaking] = useState(false);
  const [messages, setMessages] = useState([]);
  const [pendingTranscript, setPendingTranscript] = useState('');
  const [errorText, setErrorText] = useState('');
  // Fixed send word now (the editor was dropped for a cleaner card); still shown in hints.
  const sendWord = getSendWord();

  // Audio graph + recorder handles.
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const bufRef = useRef(null);
  const pollRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef('');
  const recordingRef = useRef(false);       // a segment is being captured right now
  const speechStartRef = useRef(0);
  const lastVoiceRef = useRef(0);
  const activeRef = useRef(false);           // VAD acting? true whenever we're listening
  const cancelledRef = useRef(false);
  const wakeLockRef = useRef(null);

  // Turn state.
  const turnActiveRef = useRef(false);       // true while thinking/speaking — drop segments
  const queueRef = useRef([]);               // captured segment blobs awaiting transcription
  const processingRef = useRef(false);       // the transcription worker is running
  const finalizeRef = useRef(false);         // SEND pressed: finalize once the queue drains
  const pendingTextRef = useRef('');         // accumulated transcript (mirror for callbacks)
  const sendWordRef = useRef(getSendWord());
  const messagesRef = useRef([]);
  const dateRef = useRef(defaultDate || localToday());
  const utterRef = useRef(null);
  const scrollRef = useRef(null);
  // Callback indirection so the poll/worker closures always see the latest implementations.
  const enqueueSegmentRef = useRef(null);
  const finalizeTurnRef = useRef(null);

  const pushMessage = useCallback((msg) => {
    setMessages((prev) => {
      const next = [...prev, msg];
      messagesRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pendingTranscript, status]);

  const setPending = useCallback((text) => {
    pendingTextRef.current = text;
    setPendingTranscript(text);
  }, []);

  // Back to listening after a reply finishes (or fails, or an interrupt).
  const resumeListening = useCallback(() => {
    utterRef.current = null;
    turnActiveRef.current = false;
    recordingRef.current = false;
    chunksRef.current = [];
    queueRef.current = [];
    finalizeRef.current = false;
    if (cancelledRef.current) return;
    setUserSpeaking(false);
    setStatus('listening');
    activeRef.current = true;
  }, []);

  // Speak text with the free browser voice. Mobile browsers gate speech behind a user
  // gesture and often report zero voices until the engine warms up — so resume() a paused
  // engine, pick a loaded English voice when one exists, and always fall through to onEnd
  // so a silent failure never leaves us stuck in 'speaking'.
  const speakText = useCallback((text, onEnd) => {
    const synth = window.speechSynthesis;
    const spoken = stripForSpeech(text);
    if (!synth || !spoken) { onEnd?.(); return; }
    try {
      synth.cancel();
      try { synth.resume(); } catch (e) { /* noop */ }
      const utter = new SpeechSynthesisUtterance(spoken);
      const voices = synth.getVoices?.() || [];
      const voice = voices.find((v) => /^en/i.test(v.lang)) || voices[0];
      if (voice) utter.voice = voice;
      utter.onend = () => onEnd?.();
      utter.onerror = () => onEnd?.();
      utterRef.current = utter;
      synth.speak(utter);
    } catch (e) { onEnd?.(); }
  }, []);

  // Speak the reply, then resume listening. (Server TTS is off by default — AI_TTS_ENABLED —
  // so both voice modes use the browser voice; a nicer server voice is a later upgrade.)
  const speak = useCallback((reply) => {
    if (cancelledRef.current) return;
    setStatus('speaking');
    speakText(reply, () => resumeListening());
  }, [speakText, resumeListening]);

  // End the turn: ship whatever's buffered to the conversation endpoint, speak the reply.
  const finalizeTurn = useCallback(async (reason) => {
    if (turnActiveRef.current || cancelledRef.current) return;
    turnActiveRef.current = true;
    activeRef.current = false;   // half-duplex: stop listening while we think + speak
    // Stop any in-progress capture; we've got what we need.
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') { try { r.stop(); } catch (e) { /* noop */ } }
    queueRef.current = [];
    finalizeRef.current = false;

    let text = pendingTextRef.current;
    if (reason === 'codeword') text = stripSendWord(text, sendWordRef.current);
    text = (text || '').trim();
    setPending('');

    if (!text) {
      // Nothing to send (e.g. the send word with no content before it) — keep listening.
      turnActiveRef.current = false;
      activeRef.current = true;
      setStatus('listening');
      return;
    }

    const history = messagesRef.current
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .slice(-12)
      .map((m) => ({ role: m.role, content: m.text }));

    pushMessage({ role: 'user', text });
    setStatus('thinking');

    try {
      const res = await apiClient.post('/api/health/conversation', {
        message: text,
        history,
        date: dateRef.current,
      }, { timeout: 180000 });
      if (cancelledRef.current) return;
      const { reply, entries, logged_count } = res.data;
      pushMessage({ role: 'assistant', text: reply, entries: entries || [] });
      if (logged_count > 0 && onEntrySaved) onEntrySaved();
      speak(reply);
    } catch (error) {
      if (cancelledRef.current) return;
      const s = error.response?.status;
      const detail = error.response?.data?.detail;
      if (s === 429) toast.error(detail || 'Slow down a moment — too many turns.');
      else if (s === 401) toast.error('Please log in again to keep talking.');
      else toast.error(detail || "Sorry — I couldn't log that just now. Try again.");
      resumeListening();
    }
  }, [pushMessage, onEntrySaved, speak, resumeListening, setPending]);
  finalizeTurnRef.current = finalizeTurn;

  // Transcribe one captured segment. Returns '' on silence/failure (kept quiet — a segment
  // that held no words just adds nothing to the buffer).
  const transcribeBlob = useCallback(async (blob) => {
    if (blob.size < 1200 || blob.size > MAX_UPLOAD_BYTES) return '';
    try {
      const form = new FormData();
      const type = blob.type || 'audio/webm';
      const ext = type.includes('mp4') ? 'mp4' : type.includes('ogg') ? 'ogg' : 'webm';
      form.append('audio', blob, `seg.${ext}`);
      const res = await apiClient.post('/api/health/transcribe', form, {
        timeout: 120000,
        headers: { 'Content-Type': undefined },
      });
      return (res.data?.text || '').trim();
    } catch (e) { return ''; }
  }, []);

  // Drain the segment queue one at a time (keeps transcripts in order even if the user keeps
  // talking while a segment uploads). After each append, check the buffer tail for the send
  // word; if the SEND button was pressed, finalize once the queue is empty.
  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (queueRef.current.length) {
        if (turnActiveRef.current || cancelledRef.current) { queueRef.current = []; return; }
        const blob = queueRef.current.shift();
        // eslint-disable-next-line no-await-in-loop
        const text = await transcribeBlob(blob);
        if (turnActiveRef.current || cancelledRef.current) { queueRef.current = []; return; }
        if (text) {
          const next = pendingTextRef.current ? `${pendingTextRef.current} ${text}` : text;
          setPending(next);
          if (endsWithSendWord(next, sendWordRef.current)) {
            processingRef.current = false;
            finalizeTurnRef.current?.('codeword');
            return;
          }
        }
      }
    } finally {
      processingRef.current = false;
    }
    // Queue drained. If SEND is waiting on it, finalize now.
    if (finalizeRef.current && !turnActiveRef.current && !cancelledRef.current) {
      finalizeRef.current = false;
      finalizeTurnRef.current?.('button');
    }
  }, [transcribeBlob, setPending]);

  const enqueueSegment = useCallback((blob) => {
    if (turnActiveRef.current || cancelledRef.current) return;
    queueRef.current.push(blob);
    processQueue();
  }, [processQueue]);
  enqueueSegmentRef.current = enqueueSegment;

  // A captured segment's recorder stopped: misfire vs real speech.
  const onClipStop = useCallback(() => {
    const durationMs = performance.now() - speechStartRef.current;
    const type = mimeRef.current || 'audio/webm';
    const blob = new Blob(chunksRef.current, { type });
    chunksRef.current = [];
    recordingRef.current = false;
    setUserSpeaking(false);
    if (cancelledRef.current) return;
    if (durationMs < MIN_UTTERANCE_MS || blob.size < 1200) return;  // cough/knock — ignore
    enqueueSegmentRef.current?.(blob);
  }, []);

  const startClip = useCallback(() => {
    if (recordingRef.current || cancelledRef.current || !streamRef.current || turnActiveRef.current) return;
    let recorder;
    try {
      recorder = new MediaRecorder(streamRef.current, mimeRef.current ? { mimeType: mimeRef.current } : undefined);
    } catch (e) { return; }
    chunksRef.current = [];
    recorder.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
    recorder.onstop = () => onClipStop();
    recorderRef.current = recorder;
    recordingRef.current = true;
    speechStartRef.current = performance.now();
    lastVoiceRef.current = speechStartRef.current;
    recorder.start(1000);
    setUserSpeaking(true);
  }, [onClipStop]);

  const stopClip = useCallback(() => {
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') {
      try { r.requestData(); } catch (e) { /* not all engines allow it */ }
      try { r.stop(); } catch (e) { /* already stopping */ }
    }
  }, []);

  const poll = useCallback(() => {
    if (cancelledRef.current || !activeRef.current || !analyserRef.current) return;
    const buf = bufRef.current;
    analyserRef.current.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);
    const now = performance.now();

    if (rms >= SPEECH_RMS) {
      lastVoiceRef.current = now;
      if (!recordingRef.current) startClip();
    }
    if (recordingRef.current) {
      const silentFor = now - lastVoiceRef.current;
      const total = now - speechStartRef.current;
      if (silentFor >= SILENCE_HANG_MS || total >= MAX_UTTERANCE_MS) stopClip();
    }
  }, [startClip, stopClip]);

  // SEND button — finalize with everything buffered. Flush the current segment first so the
  // last words aren't lost, then finalize once the queue drains.
  const handleSend = useCallback(() => {
    if (turnActiveRef.current || cancelledRef.current || status !== 'listening') return;
    finalizeRef.current = true;
    if (recordingRef.current) {
      stopClip();  // its segment enqueues → processQueue drains → finalize fires
    } else if (!processingRef.current && queueRef.current.length === 0) {
      finalizeRef.current = false;
      finalizeTurn('button');
    }
  }, [status, stopClip, finalizeTurn]);

  // INTERRUPT button — cut off the reply and go straight back to listening.
  const handleInterrupt = useCallback(() => {
    if (status !== 'speaking') return;
    try { window.speechSynthesis?.cancel(); } catch (e) { /* noop */ }
    resumeListening();
  }, [status, resumeListening]);

  // Boot the mic + audio graph once, on mount. (Same shape as Auto mode.)
  useEffect(() => {
    cancelledRef.current = false;
    let cleanupStream = null;
    // Nudge the speech engine to load its voice list early; some mobile browsers report an
    // empty list until this is called at least once.
    try { window.speechSynthesis?.getVoices(); } catch (e) { /* noop */ }

    const acquireWakeLock = async () => {
      try {
        if ('wakeLock' in navigator && !cancelledRef.current) {
          const sentinel = await navigator.wakeLock.request('screen');
          sentinel.addEventListener('release', () => { wakeLockRef.current = null; });
          wakeLockRef.current = sentinel;
        }
      } catch (e) { /* not critical */ }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !cancelledRef.current && !wakeLockRef.current) {
        acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    (async () => {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // autoGainControl OFF: it lifts the noise floor during quiet stretches so
          // end-of-segment never registers. Keep echo/noise suppression.
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false },
        });
      } catch (e) {
        if (!cancelledRef.current) {
          setStatus('error');
          const denied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
          setErrorText(denied
            ? 'Microphone access was denied. Allow it in your browser to have a conversation.'
            : "Couldn't reach your microphone. You can use the typed logger instead.");
        }
        return;
      }
      if (cancelledRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }

      cleanupStream = stream;
      streamRef.current = stream;
      mimeRef.current = pickMimeType();
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        if (ctx.state === 'suspended') { try { await ctx.resume(); } catch (e) { /* noop */ } }
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        source.connect(analyser);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
        bufRef.current = new Float32Array(analyser.fftSize);
      } catch (e) {
        if (!cancelledRef.current) {
          setStatus('error');
          setErrorText(`Couldn't start the audio engine: ${e?.message || 'unknown error'}`);
        }
        return;
      }

      activeRef.current = true;
      pollRef.current = setInterval(poll, POLL_MS);
      acquireWakeLock();
      setStatus('listening');
    })();

    return () => {
      cancelledRef.current = true;
      activeRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVisible);
      if (wakeLockRef.current) { try { wakeLockRef.current.release(); } catch (e) { /* noop */ } wakeLockRef.current = null; }
      try { window.speechSynthesis?.cancel(); } catch (e) { /* noop */ }
      const r = recorderRef.current;
      if (r && r.state !== 'inactive') { try { r.stop(); } catch (e) { /* noop */ } }
      if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (e) { /* noop */ } }
      const s = streamRef.current || cleanupStream;
      if (s) s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // Boot once; poll/refs carry live state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusLine = () => {
    switch (status) {
      case 'connecting': return 'Warming up the microphone…';
      case 'listening': return userSpeaking
        ? 'Your turn — I can hear you'
        : `Your turn — talk, then say “${sendWord}” or tap the green button`;
      case 'thinking': return 'Thinking…';
      case 'speaking': return 'It’s talking — tap the red button to interrupt';
      case 'error': return errorText || 'Something went wrong.';
      default: return '';
    }
  };

  const hasPending = pendingTranscript.trim().length > 0;

  return (
    <div className="flex h-full flex-col">
      {/* No title header (the toggle below already says "Walkie"). Transcript, then a
          compact status + one action button, so the checklist above stays on screen. */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && !hasPending && status !== 'error' && (
          <p className="text-center text-sm text-gray-600 dark:text-gray-300 mt-8">
            Just talk — say what you ate, how you moved, or how you slept. When you're done,
            say <span className="font-medium">“{sendWord}”</span> or tap <span className="font-medium">Send</span>.
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
              m.role === 'user'
                ? 'bg-primary-500 text-[#1a1200] rounded-br-sm'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-sm'
            }`}>
              {m.text}
              {m.entries && m.entries.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {m.entries.map((e, j) => (
                    <span key={j} className="inline-flex items-center rounded-full bg-white/70 dark:bg-gray-900/50 border border-primary-200 dark:border-primary-800 px-2 py-0.5 text-sm font-medium text-primary-800 dark:text-primary-200">
                      {entryLabel(e)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {/* Live, not-yet-sent transcript — dashed to read as in-progress. */}
        {hasPending && (
          <div className="flex justify-end">
            <div className="max-w-[85%] rounded-2xl rounded-br-sm border-2 border-dashed border-primary-400 dark:border-primary-600 px-4 py-2.5 text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">
              {pendingTranscript}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-surface-border dark:border-surface-dark-border px-4 py-2.5 flex flex-col gap-2">
        {/* Compact status row: who's talking + a small End. Color-matched to the action
            button (green = your turn, red = it's talking). */}
        <div className="flex items-center gap-2">
          <WalkieOrb status={status} userSpeaking={userSpeaking} />
          <p className={`min-w-0 flex-1 text-sm font-bold ${
            status === 'error' ? 'text-red-600 dark:text-red-400'
              : status === 'speaking' ? 'text-red-600 dark:text-red-400'
              : status === 'thinking' ? 'text-gray-600 dark:text-gray-300'
              : 'text-green-700 dark:text-green-400'
          }`}>
            {statusLine()}
          </p>
          {status !== 'error' && (
            <Button variant="ghost" onClick={onClose} className="shrink-0 text-sm" title="End the conversation">End</Button>
          )}
        </div>

        {status === 'error' ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-gray-300 dark:border-surface-dark-border px-6 h-12 text-base font-semibold text-gray-700 dark:text-gray-200"
          >
            Close
          </button>
        ) : status === 'speaking' ? (
          <button
            type="button"
            onClick={handleInterrupt}
            className="flex w-full max-w-md items-center justify-center gap-3 h-14 rounded-2xl bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-xl font-extrabold tracking-wide shadow-lg transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
              <path d="M6 6h12v12H6z" />
            </svg>
            Interrupt
          </button>
        ) : status === 'thinking' ? (
          <button
            type="button"
            disabled
            className="w-full max-w-md h-14 rounded-2xl bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xl font-extrabold cursor-default"
          >
            Thinking…
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSend}
            disabled={status !== 'listening' || !hasPending}
            className="flex w-full max-w-md items-center justify-center gap-3 h-14 rounded-2xl bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-xl font-extrabold tracking-wide shadow-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
              <path d="M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404Z" />
            </svg>
            Send
          </button>
        )}
      </div>
    </div>
  );
};

const WalkieOrb = ({ status, userSpeaking }) => {
  if (status === 'listening' && userSpeaking) {
    return (
      <span className="flex items-end gap-0.5 h-7 shrink-0" aria-label="hearing you">
        {[0, 1, 2, 3, 4].map((i) => (
          <span key={i} className="w-1.5 rounded-full bg-green-500 animate-pulse"
            style={{ height: `${8 + (i % 2 === 0 ? 8 : 16)}px`, animationDelay: `${i * 90}ms` }} />
        ))}
      </span>
    );
  }
  if (status === 'thinking') {
    return (
      <span className="flex items-center gap-1 h-7 shrink-0" aria-label="thinking">
        {[0, 1, 2].map((i) => (
          <span key={i} className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />
        ))}
      </span>
    );
  }
  const color = status === 'error' ? 'bg-red-500'
    : status === 'connecting' ? 'bg-gray-400'
    : status === 'speaking' ? 'bg-red-500' : 'bg-green-500';
  const ping = status === 'listening' || status === 'speaking' || status === 'connecting';
  return (
    <span className="relative flex h-7 w-7 items-center justify-center shrink-0" aria-label={status}>
      {ping && <span className={`absolute inline-flex h-7 w-7 rounded-full opacity-30 ${color} animate-ping`} />}
      <span className={`relative inline-flex h-5 w-5 rounded-full ${color}`} />
    </span>
  );
};

export default WalkieTalkie;
